import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { detectorCorners, footprintOnPlane } from "../geometry/vxGeom";
import "./CanvasPane.css";

const COLOR_INCLINED = 0xf97362;
const COLOR_COPLANAR = 0x5eead4;
const COLOR_GIMBAL = 0xf5a623;
const COLOR_BORDER = 0x4c4c4c;
const COLOR_AXIS_HORIZONTAL = 0x5d7f2d;
const COLOR_AXIS_VERTICAL = 0x7a3d45;

// Custom axes (replaces THREE.AxesHelper's default red/green/blue) so the
// ground-plane axes and the up axis get the app's own palette instead.
function buildAxes() {
  const positions = new Float32Array([
    // horizontal: world X
    0, 0, 0, 1, 0, 0,
    // horizontal: world Z
    0, 0, 0, 0, 0, 1,
    // vertical: world Y
    0, 0, 0, 0, 1, 0,
  ]);
  const colors = new Float32Array(6 * 3);
  const setColor = (i, hex) => {
    const c = new THREE.Color(hex);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  };
  for (let i = 0; i < 4; i++) setColor(i, COLOR_AXIS_HORIZONTAL);
  setColor(4, COLOR_AXIS_VERTICAL);
  setColor(5, COLOR_AXIS_VERTICAL);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const mat = new THREE.LineBasicMaterial({ vertexColors: true });
  return new THREE.LineSegments(geo, mat);
}

// True-to-scale axis remap only (native Z -> world Y, native Y -> world -Z).
// No distance compression: any nonlinear per-point remap breaks either
// flatness (a rigid detector plane) or ray-straightness (source-to-corner
// lines), and both must hold for the picture to be geometrically trustworthy.
function toWorld([x, y, z]) {
  return [x, z, -y];
}

function buildRig(color) {
  const group = new THREE.Group();

  const srcGeo = new THREE.SphereGeometry(0.35, 16, 16);
  const srcMat = new THREE.MeshBasicMaterial({ color });
  const srcMesh = new THREE.Mesh(srcGeo, srcMat);
  group.add(srcMesh);

  const detGeo = new THREE.BufferGeometry();
  detGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(12), 3));
  detGeo.setIndex([0, 1, 2, 0, 2, 3]);
  const detMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.35,
    side: THREE.DoubleSide,
  });
  const detMesh = new THREE.Mesh(detGeo, detMat);
  group.add(detMesh);

  const detEdges = new THREE.LineLoop(
    new THREE.BufferGeometry().setAttribute("position", new THREE.BufferAttribute(new Float32Array(12), 3)),
    new THREE.LineBasicMaterial({ color })
  );
  group.add(detEdges);

  const rayPositions = new Float32Array(4 * 2 * 3);
  const rayGeo = new THREE.BufferGeometry();
  rayGeo.setAttribute("position", new THREE.BufferAttribute(rayPositions, 3));
  const rayMat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.5 });
  const rays = new THREE.LineSegments(rayGeo, rayMat);
  group.add(rays);

  const fpGeo = new THREE.BufferGeometry();
  fpGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(12), 3));
  fpGeo.setIndex([0, 1, 2, 0, 2, 3]);
  const fpMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.4,
    side: THREE.DoubleSide,
  });
  const fpMesh = new THREE.Mesh(fpGeo, fpMat);
  group.add(fpMesh);

  const uAxisGeo = new THREE.BufferGeometry();
  uAxisGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
  const uAxis = new THREE.Line(uAxisGeo, new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.7 }));
  group.add(uAxis);

  const vAxisGeo = new THREE.BufferGeometry();
  vAxisGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
  const vAxis = new THREE.Line(vAxisGeo, new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.4 }));
  group.add(vAxis);

  return { group, srcMesh, detMesh, detEdges, rays, fpMesh, uAxis, vAxis };
}

function setPos(array, i, [x, y, z]) {
  array[i * 3] = x;
  array[i * 3 + 1] = y;
  array[i * 3 + 2] = z;
}

function extentsOf(vecs, halfU, halfV) {
  let nearExtent = 0;
  let farExtent = 0;
  for (const vec of vecs) {
    const S = [vec[0], vec[1], vec[2]];
    nearExtent = Math.max(nearExtent, Math.hypot(S[0], S[1], S[2]));
    const corners = detectorCorners(vec, halfU, halfV);
    for (const c of corners) {
      farExtent = Math.max(farExtent, Math.hypot(c[0], c[1], c[2]));
    }
  }
  return { nearExtent, farExtent: Math.max(farExtent, nearExtent) };
}

// Fit the camera + grid/axes scale to either the near (source/object-plane)
// region or the overall (source-to-detector) extent. "near" keeps the
// cone-tip legible even when SOD is 1000x smaller than SDD; "overall" pulls
// back far enough to see the whole rig, source and detector alike.
function fitCamera(st, nearExtent, farExtent, mode) {
  if (nearExtent <= 0) return;
  const extent = mode === "overall" ? farExtent : nearExtent;

  st.gridHelper.scale.setScalar(farExtent * 2);
  st.axesHelper.scale.setScalar(Math.max(extent * 0.3, nearExtent * 1.5));
  st.origin.scale.setScalar(nearExtent * 0.05);

  const camDist = mode === "overall" ? farExtent * 1.8 : nearExtent * 3;
  st.camera.position.set(camDist * 0.6, camDist * 0.5, camDist * 0.6);
  st.camera.near = Math.max(nearExtent * 0.005, 1e-4);
  st.camera.far = farExtent * 20;
  st.camera.updateProjectionMatrix();
  st.controls.target.set(0, 0, 0);
  st.controls.minDistance = nearExtent * 0.05;
  st.controls.maxDistance = farExtent * 10;
  st.controls.update();

  const srcRadius = nearExtent * 0.03;
  for (const rig of [st.inclinedRig, st.coplanarRig, st.gimbalRig]) {
    rig.srcMesh.geometry.dispose();
    rig.srcMesh.geometry = new THREE.SphereGeometry(srcRadius, 16, 16);
  }
}

function updateRig(rig, vec, halfU, halfV) {
  const S = [vec[0], vec[1], vec[2]];
  const D = [vec[3], vec[4], vec[5]];
  const U = [vec[6], vec[7], vec[8]];
  const V = [vec[9], vec[10], vec[11]];
  const corners = detectorCorners(vec, halfU, halfV);

  const Sw = toWorld(S);
  rig.srcMesh.position.set(Sw[0], Sw[1], Sw[2]);

  const cornersW = corners.map(toWorld);
  const detPos = rig.detMesh.geometry.attributes.position.array;
  const edgePos = rig.detEdges.geometry.attributes.position.array;
  cornersW.forEach((c, i) => {
    setPos(detPos, i, c);
    setPos(edgePos, i, c);
  });
  rig.detMesh.geometry.attributes.position.needsUpdate = true;
  rig.detEdges.geometry.attributes.position.needsUpdate = true;

  const rayPos = rig.rays.geometry.attributes.position.array;
  cornersW.forEach((c, i) => {
    setPos(rayPos, i * 2, Sw);
    setPos(rayPos, i * 2 + 1, c);
  });
  rig.rays.geometry.attributes.position.needsUpdate = true;

  const fp = footprintOnPlane(vec, halfU, halfV, 0);
  const fpPos = rig.fpMesh.geometry.attributes.position.array;
  if (fp) {
    fp.forEach(([x, y], i) => setPos(fpPos, i, toWorld([x, y, 0])));
    rig.fpMesh.visible = true;
  } else {
    rig.fpMesh.visible = false;
  }
  rig.fpMesh.geometry.attributes.position.needsUpdate = true;

  const Dw = toWorld(D);
  const uTipW = toWorld([D[0] + U[0] * halfU, D[1] + U[1] * halfU, D[2] + U[2] * halfU]);
  const vTipW = toWorld([D[0] + V[0] * halfV, D[1] + V[1] * halfV, D[2] + V[2] * halfV]);

  const uPos = rig.uAxis.geometry.attributes.position.array;
  setPos(uPos, 0, Dw);
  setPos(uPos, 1, uTipW);
  rig.uAxis.geometry.attributes.position.needsUpdate = true;

  const vPos = rig.vAxis.geometry.attributes.position.array;
  setPos(vPos, 0, Dw);
  setPos(vPos, 1, vTipW);
  rig.vAxis.geometry.attributes.position.needsUpdate = true;
}

const Scene3D = forwardRef(function Scene3D(
  {
    inclinedVecs,
    coplanarVecs,
    gimbalVecs,
    showInclined,
    showCoplanar,
    showGimbal,
    halfU,
    halfV,
    theta,
    numAngles,
  },
  ref
) {
  const mountRef = useRef(null);
  const stateRef = useRef(null);
  const fittedRef = useRef(false);

  useEffect(() => {
    const mount = mountRef.current;
    // StrictMode replays this effect with a fresh set of Three.js objects.
    // The replacement camera needs its own initial fit.
    fittedRef.current = false;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x373737);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100000);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    mount.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    const gridHelper = new THREE.GridHelper(1, 20, COLOR_BORDER, COLOR_BORDER);
    scene.add(gridHelper);

    const axesHelper = buildAxes();
    scene.add(axesHelper);

    const origin = new THREE.Mesh(
      new THREE.SphereGeometry(1, 12, 12),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    );
    scene.add(origin);

    const inclinedRig = buildRig(COLOR_INCLINED);
    const coplanarRig = buildRig(COLOR_COPLANAR);
    const gimbalRig = buildRig(COLOR_GIMBAL);
    scene.add(inclinedRig.group);
    scene.add(coplanarRig.group);
    scene.add(gimbalRig.group);

    const resize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      // Flex/absolute layout can briefly report zero while the pane settles.
      // ResizeObserver will call us again once usable dimensions are available.
      if (w <= 0 || h <= 0) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);

    let raf;
    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    stateRef.current = {
      scene,
      camera,
      renderer,
      controls,
      inclinedRig,
      coplanarRig,
      gimbalRig,
      gridHelper,
      axesHelper,
      origin,
      resizeObserver,
      raf,
    };

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
      if (stateRef.current?.renderer === renderer) stateRef.current = null;
      fittedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    st.inclinedRig.group.visible = showInclined;
    st.coplanarRig.group.visible = showCoplanar;
    st.gimbalRig.group.visible = showGimbal;
  }, [showInclined, showCoplanar, showGimbal]);

  useEffect(() => {
    const st = stateRef.current;
    if (!st || inclinedVecs.length === 0) return;
    const idx = Math.round((theta / 360) * numAngles) % numAngles;
    updateRig(st.inclinedRig, inclinedVecs[idx], halfU, halfV);
    updateRig(st.coplanarRig, coplanarVecs[idx], halfU, halfV);
    updateRig(st.gimbalRig, gimbalVecs[idx], halfU, halfV);

    const { nearExtent, farExtent } = extentsOf(
      [...inclinedVecs, ...coplanarVecs, ...gimbalVecs],
      halfU,
      halfV
    );
    st.nearExtent = nearExtent;
    st.farExtent = farExtent;

    // Fit the camera once per mount to the near (source/object-plane) region
    // rather than the whole detector orbit: with real CBCT params SOD is
    // often 1000x smaller than SDD, so a whole-scene fit leaves the cone-tip
    // sub-pixel. Default view is tight on the source; the FOV/Overall buttons
    // (or manual scroll) reveal the detector, placed correctly at true scale.
    if (!fittedRef.current && nearExtent > 0) {
      fittedRef.current = true;
      fitCamera(st, nearExtent, farExtent, "near");
    }
  }, [inclinedVecs, coplanarVecs, gimbalVecs, theta, numAngles, halfU, halfV]);

  useImperativeHandle(ref, () => ({
    focusFov() {
      const st = stateRef.current;
      if (!st || !st.nearExtent) return;
      fitCamera(st, st.nearExtent, st.farExtent, "near");
    },
    focusOverall() {
      const st = stateRef.current;
      if (!st || !st.nearExtent) return;
      fitCamera(st, st.nearExtent, st.farExtent, "overall");
    },
  }));

  return (
    <div className="canvas-mount" ref={mountRef}>
      <div className="canvas-label">Geometry</div>
      <div className="canvas-legend">
        <span className="legend-item"><span className="legend-dot" style={{ background: "#f97362" }} /> Inclined</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: "#5EEAD4" }} /> Coplanar</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: "#F5A623" }} /> Gimbal Lock</span>
      </div>
    </div>
  );
});

export default Scene3D;
